import React, { useCallback } from 'react';
import ReactDOM from 'react-dom';

type Props = {
  children: React.ReactNode;
};

const BANNER_SELECTOR = `#newron-banner`;

function ClientBanner({ children }: Props): React.ReactPortal | null {
  const [isInDocument, setIsInDocument] = React.useState<boolean>(false);
  const timer = React.useRef<ReturnType<typeof setTimeout>>(null);

  React.useEffect(() => {
    const checkInDocument = () => {
      const el = document.querySelector(BANNER_SELECTOR);
      if (!el) {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(checkInDocument, 50);
        setIsInDocument(false);
        return;
      } else {
        setIsInDocument(true);
      }
    };
    checkInDocument();
  }, []);

  if (!isInDocument) {
    return null;
  }

  const el = document.querySelector(BANNER_SELECTOR);

  if (!el) {
    console.warn('Newron banner could not be located in the HTML');
    return null;
  }

  return ReactDOM.createPortal(<BannerWrapper>{children}</BannerWrapper>, el);
}

type WrapperProps = {
  children: React.ReactNode;
};
function BannerWrapper({ children }: WrapperProps) {
  const resizeThrottle = React.useRef<ReturnType<typeof setTimeout>>(null);
  const el = React.useRef<HTMLDivElement>();
  const ob = React.useRef<MutationObserver>();

  const adjustToBannerHeight = useCallback(() => {
    if (el.current) {
      document.documentElement.style.setProperty('--banner-height', `${el.current.parentElement.clientHeight}px`);
    }
  }, []);

  React.useEffect(() => {
    if (el.current && !ob.current) {
      ob.current = new MutationObserver(mutationList => {
        adjustToBannerHeight();
      });

      ob.current.observe(el.current, { attributes: true, childList: true, subtree: true });
    }

    return () => {
      if (ob.current) {
        ob.current.disconnect();
      }
    };
  }, [el, ob, adjustToBannerHeight]);

  React.useLayoutEffect(() => {
    const handleResize = () => {
      clearTimeout(resizeThrottle.current);
      resizeThrottle.current = setTimeout(adjustToBannerHeight, 10);
    };

    window.addEventListener('resize', handleResize);
    adjustToBannerHeight();

    return () => {
      clearTimeout(resizeThrottle.current);
      window.removeEventListener('resize', handleResize);
      document.documentElement.style.setProperty('--banner-height', '0');
    };
  }, [adjustToBannerHeight]);

  return <div ref={el}>{children}</div>;
}
export { ClientBanner };
